import { CheckIcon, ShirtIcon } from "@xola/icons";
import { sortBy } from "lodash";
import React from "react";
import {
    Audit,
    FulfillmentUnitStatus,
    MerchandiseFulfillmentPolicy,
    MerchandisePurchaseItem,
    PurchaseItem,
} from "types";
import { ItemProductImage } from "../../PurchaseProductImage";
import { TimelinePill } from "../TimelineItem";
import { getMerchandiseWareLineItems, isMerchandisePurchaseItemFulfilled } from "@/models/MerchandisePurchaseItem";
import { isMerchandiseItem } from "@/models/PurchaseItem";

interface AuditLineItemDelta {
    id?: ID;
    old: null | { id?: ID; type?: string };
    new: null | { id?: ID; type?: string };
    quantity?: { old?: number; new?: number };
}

interface AuditFulfillmentUnitDelta {
    id?: ID;
    status?: { old?: string; new?: string };
}

interface AuditItemDelta {
    id: ID;
    lineItems?: Record<string, AuditLineItemDelta>;
    fulfillmentUnits?: Record<string, AuditFulfillmentUnitDelta>;
}

interface MerchandiseCreatedTimelineItemProps {
    item: PurchaseItem;
    audits: Audit[];
}

/**
 * Returns the set of line item IDs that were added AFTER purchase creation
 * by scanning audits for entries with { old: null, new: { id, type: "ware" } }.
 */
const getPostCreationLineItemIds = (itemId: ID, audits: Audit[]): Set<ID> => {
    const ids = new Set<ID>();

    for (const audit of audits) {
        const auditItems: AuditItemDelta[] = Array.isArray(audit.data?.items)
            ? audit.data.items
            : Object.values(audit.data?.items ?? {});

        const auditItem = auditItems.find((ai) => ai.id === itemId);
        if (!auditItem?.lineItems) {
            continue;
        }

        for (const lineItem of Object.values(auditItem.lineItems)) {
            if (lineItem.old === null && lineItem.new?.id && lineItem.new?.type === "ware") {
                ids.add(lineItem.new.id);
            }
        }
    }

    return ids;
};

/**
 * Returns the quantity of a WARE line item at creation time by finding the earliest
 * audit that recorded a quantity change for it and using quantity.old.
 * Falls back to null if no audit ever changed it (current quantity = original).
 */
const getOriginalQuantity = (lineItemId: ID, itemId: ID, sortedAudits: Audit[]): number | null => {
    for (const audit of sortedAudits) {
        const auditItems: AuditItemDelta[] = Array.isArray(audit.data?.items)
            ? audit.data.items
            : Object.values(audit.data?.items ?? {});

        const auditItem = auditItems.find((auditItem) => auditItem.id === itemId);
        if (!auditItem?.lineItems) {
            continue;
        }

        const auditLineItem = Object.values(auditItem.lineItems).find((li) => li.id === lineItemId);
        if (auditLineItem?.quantity?.old !== undefined) {
            return auditLineItem.quantity.old;
        }
    }

    return null;
};

/**
 * Returns the IDs of fulfillment units that, at some point, moved out of "processing_fulfillment"
 * into anything other than "fulfilled" — i.e. an auto-fulfillment attempt that was rolled back
 * (typically because inventory wasn't available). This is unambiguous, timing-independent proof
 * that whatever LATER fulfilled that unit was a separate, subsequent action: a genuine
 * auto-fulfillment-at-creation success goes "processing_fulfillment" -> "fulfilled" directly and
 * never touches this rolled-back state. Timing alone can't make this distinction — a seller can
 * complete a manual fulfillment within seconds of creation just as easily as the backend can
 * finish an automatic one.
 */
const getFailedFulfillmentAttemptUnitIds = (item: MerchandisePurchaseItem, audits: Audit[]): Set<ID> => {
    const unitIds = new Set<ID>();

    for (const audit of audits) {
        const auditItems: AuditItemDelta[] = Array.isArray(audit.data?.items)
            ? audit.data.items
            : Object.values(audit.data?.items ?? {});

        const auditItem = auditItems.find((ai) => ai.id === item.id);
        if (!auditItem?.fulfillmentUnits) {
            continue;
        }

        for (const unitDelta of Object.values(auditItem.fulfillmentUnits)) {
            if (
                unitDelta.id &&
                unitDelta.status?.old === FulfillmentUnitStatus.PROCESSING_FULFILLMENT &&
                unitDelta.status?.new !== FulfillmentUnitStatus.FULFILLED
            ) {
                unitIds.add(unitDelta.id);
            }
        }
    }

    return unitIds;
};

/**
 * Returns the set of template IDs whose fulfillment units were fulfilled by a separate, later
 * action rather than auto-fulfilled at creation — identified by the unit having previously failed
 * an auto-fulfillment attempt (see getFailedFulfillmentAttemptUnitIds). Used to tell apart line
 * items that were auto-fulfilled at creation from ones completed later, since the item-level
 * fulfillmentStatus alone can't distinguish the two: both pass through the same
 * MerchandiseFulfillmentStatus.PARTIALLY_FULFILLED intermediate value.
 */
const getLateFulfilledTemplateIds = (item: MerchandisePurchaseItem, audits: Audit[]): Set<ID> => {
    const templateIdByUnitId = new Map<ID, ID>();
    for (const unit of item.fulfillmentUnits ?? []) {
        if (unit.template?.id) {
            templateIdByUnitId.set(unit.id, unit.template.id);
        }
    }

    const failedAttemptUnitIds = getFailedFulfillmentAttemptUnitIds(item, audits);
    const templateIds = new Set<ID>();

    for (const audit of audits) {
        const auditItems: AuditItemDelta[] = Array.isArray(audit.data?.items)
            ? audit.data.items
            : Object.values(audit.data?.items ?? {});

        const auditItem = auditItems.find((ai) => ai.id === item.id);
        if (!auditItem?.fulfillmentUnits) {
            continue;
        }

        for (const unitDelta of Object.values(auditItem.fulfillmentUnits)) {
            if (
                unitDelta.status?.new !== FulfillmentUnitStatus.FULFILLED ||
                !unitDelta.id ||
                !failedAttemptUnitIds.has(unitDelta.id)
            ) {
                continue;
            }

            const templateId = templateIdByUnitId.get(unitDelta.id);
            if (templateId) {
                templateIds.add(templateId);
            }
        }
    }

    return templateIds;
};

/**
 * Returns true when the item was auto-fulfilled at creation time.
 */
const isAutoFulfilledAtCreation = (item: PurchaseItem, audits: Audit[], originalTemplateIds: Set<ID>): boolean => {
    if (!isMerchandiseItem(item) || !isMerchandisePurchaseItemFulfilled(item)) {
        return false;
    }

    if (item.fulfillmentPolicy !== MerchandiseFulfillmentPolicy.IMMEDIATE) {
        return false;
    }

    const lateFulfilledTemplateIds = getLateFulfilledTemplateIds(item, audits);
    return ![...originalTemplateIds].some((templateId) => lateFulfilledTemplateIds.has(templateId));
};

export const MerchandiseCreatedTimelineItem = ({ item, audits }: MerchandiseCreatedTimelineItemProps) => {
    if (!isMerchandiseItem(item)) {
        return null;
    }

    const sortedAudits = sortBy(audits, (a) => a.createdAt.valueOf());
    const postCreationIds = getPostCreationLineItemIds(item.id, audits);
    const originalLineItems = getMerchandiseWareLineItems(item).filter((lineItem) => !postCreationIds.has(lineItem.id));
    const variants = originalLineItems.map((lineItem) => {
        const qty = getOriginalQuantity(lineItem.id, item.id, sortedAudits) ?? lineItem.quantity;
        return `${qty}x ${lineItem.name}`;
    });

    if (variants.length === 0) {
        return null;
    }

    const originalTemplateIds = new Set(
        originalLineItems.map((lineItem) => lineItem.template?.id).filter((id): id is ID => !!id),
    );
    const isAutoFulfilled = isAutoFulfilledAtCreation(item, audits, originalTemplateIds);

    return (
        <div className="space-y-2">
            <div className="flex w-full max-w-lg items-center space-x-3 rounded bg-gray-lighter p-3">
                <ItemProductImage item={item} size="tiny" className="!mr-0" classNames={{ image: "h-8 w-8" }} />
                <div className="text-sm font-bold text-gray-darker">
                    <var data-var="item-name">{item.name}</var>
                </div>
            </div>

            {variants.map((variantLine) => (
                <div key={variantLine} className="text-sm text-gray-dark">
                    <TimelinePill>
                        <span className="flex max-w-200 items-start">
                            <ShirtIcon className="mr-1 mt-0.5 shrink-0" />
                            <var data-var="variant-line" className="whitespace-normal break-words">
                                {variantLine}
                            </var>
                        </span>
                    </TimelinePill>
                </div>
            ))}

            {isAutoFulfilled && (
                <TimelinePill className="!bg-green-lightish text-white">
                    <CheckIcon className="mr-1" />
                    Auto Fulfilled
                </TimelinePill>
            )}
        </div>
    );
};
